import { CAMERA_STYLE, TICK_HZ, toMove, type ChessColor, type PlayerId, type PromotionPiece, type State, type Winner } from '@chessshooter/shared';
import type { BoardLayout } from './chess/boardLayout';
import { pieceUrl } from './chess/pieces';
import { phaseCountdown, statusLine } from './chess/status';
import { HitFeedback, formatHitLoss } from './hitFeedback';
import { bindFullscreenButton } from './fullscreen';
import type { Settings } from './settings';
import { BOARD_STYLE, LOW_CLOCK_MS, WEAPON_STYLE } from './uiConstants';

// DOM HUD: per player the chess clock (the match's only resource) and the weapon, the phase timer, the
// status line (whose move it is at the board, the countdown into the next phase), the promotion picker,
// the result overlay and the settings sheet for the touch scheme (plus the fullscreen toggle, which the
// lobby offers too).

export interface HudPrefs {
  /** mutated in place when a switch is flipped, so the input model sees the change at once */
  settings: Settings;
  onChange: () => void;
}

/** What the HUD needs to know about the board on screen. */
export interface BoardHud {
  layout: BoardLayout;
  /** the side to move is in check */
  check: boolean;
}

const PROMOTIONS: { piece: PromotionPiece; code: 'Q' | 'R' | 'B' | 'N'; label: string }[] = [
  { piece: 'q', code: 'Q', label: 'Dame' },
  { piece: 'r', code: 'R', label: 'Turm' },
  { piece: 'b', code: 'B', label: 'Läufer' },
  { piece: 'n', code: 'N', label: 'Springer' },
];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent: HTMLElement): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  parent.appendChild(node);
  return node;
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/** Restarts a CSS animation even when the trigger comes faster than the animation runs. */
function replay(node: HTMLElement, cls: string): void {
  node.classList.remove(cls);
  void node.offsetWidth;
  node.classList.add(cls);
}

interface Side {
  panel: HTMLElement;
  name: HTMLElement;
  weapon: HTMLElement;
  clock: HTMLElement;
  penalty: HTMLElement;
}

export function createHud(root: HTMLElement, prefs: HudPrefs) {
  root.replaceChildren();
  root.style.setProperty('--hud-full-height', `${CAMERA_STYLE.hudHeight}px`);
  root.style.setProperty('--hud-compact-height', `${CAMERA_STYLE.hudCompactHeight}px`);
  const bar = el('div', 'hud-bar', root);
  const makeSide = (cls: string, label: string): Side => {
    const side = el('div', `hud-side ${cls}`, bar);
    const heading = el('div', 'hud-heading', side);
    const name = el('div', 'hud-name', heading);
    name.textContent = label;
    const weapon = el('span', 'hud-weapon', heading);
    el('div', 'hud-clock-label', side).textContent = 'Restzeit';
    const row = el('div', 'hud-row', side);
    const penalty = el('span', 'hud-penalty', side);
    penalty.setAttribute('role', 'status');
    return { panel: side, name, weapon, clock: el('span', 'hud-clock', row), penalty };
  };
  const left = makeSide('hud-me', 'Du');
  const centre = el('div', 'hud-centre', bar);
  const phase = el('div', 'hud-phase', centre);
  const timer = el('div', 'hud-timer', centre);
  const right = makeSide('hud-foe', 'Bot');
  const surrender = el('button', 'hud-secondary hud-surrender hidden', bar);
  surrender.type = 'button';
  surrender.textContent = 'Aufgeben';
  surrender.title = 'Match aufgeben';
  let onSurrender: (() => void) | null = null;
  surrender.addEventListener('click', () => {
    if (!surrender.disabled && !surrender.classList.contains('hidden')) onSurrender?.();
  });
  let foeName = 'Bot';
  const gear = el('button', 'hud-gear', bar);
  gear.type = 'button';
  gear.title = 'Einstellungen';
  gear.setAttribute('aria-label', 'Einstellungen');
  gear.textContent = '⚙';

  const sheet = el('div', 'hud-settings hidden', root);
  const sheetBox = el('div', 'hud-settings-box', sheet);
  el('div', 'hud-settings-title', sheetBox).textContent = 'Einstellungen';
  const addSwitch = (key: keyof Settings, label: string): void => {
    const row = el('label', 'hud-settings-row', sheetBox);
    const box = el('input', 'hud-settings-check', row);
    box.type = 'checkbox';
    box.checked = prefs.settings[key] ?? false;
    box.addEventListener('change', () => {
      prefs.settings[key] = box.checked;
      prefs.onChange();
    });
    el('span', 'hud-settings-label', row).textContent = label;
  };
  addSwitch('movePadLocked', 'Lauf-Pad fest in der Ecke');
  addSwitch('aimPadLocked', 'Ziel-Pad fest in der Ecke');
  addSwitch('aimLine', 'Schussvorschau mit Waffe');
  addSwitch('chessSounds', 'Schach-Sounds');
  addSwitch('arenaSounds', 'Arena-Sounds');
  addSwitch('overview', 'Ganze Arena anzeigen');
  const fullscreen = el('button', 'hud-secondary hud-fullscreen', sheetBox);
  fullscreen.type = 'button';
  bindFullscreenButton(fullscreen);
  const close = el('button', 'hud-close', sheetBox);
  close.type = 'button';
  close.textContent = 'Schließen';
  gear.addEventListener('click', () => sheet.classList.toggle('hidden'));
  close.addEventListener('click', () => sheet.classList.add('hidden'));

  const hints = el('div', 'hud-touch-hints', root);
  el('span', '', hints).textContent = 'BEWEGEN';
  el('span', '', hints).textContent = 'ZIELEN · LOSLASSEN';

  const net = el('div', 'hud-net', root);

  // whose move it is above the board, or the countdown into the next phase under the bar
  const status = el('div', 'hud-status hidden', root);
  const statusText = el('span', 'hud-status-text', status);

  const countdown = el('div', 'hud-countdown hidden', root);
  countdown.setAttribute('aria-hidden', 'true');
  const countdownText = el('span', 'hud-countdown-text', countdown);

  const promotion = el('div', 'hud-promotion hidden', root);
  const promotionBox = el('div', 'hud-promotion-box', promotion);
  el('div', 'hud-promotion-title', promotionBox).textContent = 'Umwandlung';
  const promotionRow = el('div', 'hud-promotion-row', promotionBox);
  let onPromotion: ((piece: PromotionPiece) => void) | null = null;
  let onPromotionCancel: (() => void) | null = null;
  let promotionPressed = false;
  // A touch that opens the picker can synthesize a click on the new overlay. Only a fresh press
  // inside the picker (or keyboard activation, detail 0) may choose or cancel a promotion.
  promotion.addEventListener('pointerdown', () => { promotionPressed = true; }, { capture: true });
  promotion.addEventListener('pointercancel', () => { promotionPressed = false; });
  const promotionImages = PROMOTIONS.map(({ piece, code, label }) => {
    const button = el('button', 'hud-promo', promotionRow);
    button.type = 'button';
    button.title = label;
    button.setAttribute('aria-label', label);
    const img = el('img', 'hud-promo-img', button);
    img.alt = label;
    img.draggable = false;
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      if (promotionPressed || e.detail === 0) onPromotion?.(piece);
      promotionPressed = false;
    });
    return { img, code };
  });
  promotion.addEventListener('click', (e) => {
    if (promotionPressed || e.detail === 0) onPromotionCancel?.();
    promotionPressed = false;
  });
  promotionBox.addEventListener('click', (e) => e.stopPropagation());

  const overlay = el('div', 'hud-result hidden', root);
  const box = el('div', 'hud-result-box', overlay);
  const title = el('div', 'hud-result-title', box);
  const detail = el('div', 'hud-result-detail hidden', box);
  const again = el('button', 'hud-again', box);
  again.type = 'button';
  again.textContent = 'Zurück zum Menü';
  let onAgain: (() => void) | null = null;
  again.addEventListener('click', () => onAgain?.());

  function updateSide(side: Side, state: State, id: PlayerId, running: boolean): void {
    const p = state.players[id];
    side.panel.classList.toggle('running', running);
    side.panel.classList.toggle('low', p.chessClockMs < LOW_CLOCK_MS);
    setText(side.weapon, p.weapon ? `[${WEAPON_STYLE[p.weapon].letter}]` : '[ ]');
    const weaponName = p.weapon ? WEAPON_STYLE[p.weapon].name : 'Waffe aufsammeln';
    if (side.weapon.title !== weaponName) {
      side.weapon.title = weaponName;
      side.weapon.setAttribute('aria-label', weaponName);
    }
    setText(side.clock, formatClock(p.chessClockMs));
    side.clock.classList.toggle('running', running);
    side.clock.classList.toggle('low', p.chessClockMs < LOW_CLOCK_MS);
  }

  /** Keep confirmed losses visible even when the same snapshot enters a phase transition. */
  function flashPenalty(side: Side, ms: number): void {
    side.penalty.textContent = formatHitLoss(ms);
    replay(side.panel, 'hit');
    replay(side.penalty, 'show');
    replay(side.clock, 'hit');
  }

  const hits = new HitFeedback();
  let statusPlace = '';

  function update(state: State, me: PlayerId, board: BoardHud | null = null): void {
    surrender.classList.toggle('hidden', state.winner !== null || onSurrender === null);
    const foe: PlayerId = me === 0 ? 1 : 0;
    const mover = state.transitionTicks === 0 && state.phase === 'chess' && state.winner === null ? toMove(state) : null;
    updateSide(left, state, me, mover === me);
    updateSide(right, state, foe, mover === foe);
    const losses = hits.update(state);
    if (losses[me] > 0) flashPenalty(left, losses[me]);
    if (losses[foe] > 0) flashPenalty(right, losses[foe]);
    setText(phase, state.phase === 'shooter' ? 'Arena' : 'Schach');
    const remaining = Math.max(0, state.phaseEndsAtTick - state.tick);
    setText(timer, formatClock((remaining / TICK_HZ) * 1000));
    const seconds = phaseCountdown(state);
    timer.classList.toggle('warn', seconds !== null);
    countdown.classList.toggle('hidden', seconds === null);
    if (seconds === null) {
      setText(countdownText, '');
      countdownText.classList.remove('show');
    } else if (countdownText.textContent !== String(seconds)) {
      setText(countdownText, String(seconds));
      replay(countdownText, 'show');
    }

    const line = statusLine(state, me, foeName, board?.check ?? false);
    status.classList.toggle('hidden', line === null);
    if (line) {
      setText(statusText, line.text);
      const cls = `hud-status-text hud-status-${line.tone}`;
      if (statusText.className !== cls) statusText.className = cls;
    }
    // above the board while it is on screen, otherwise centred under the bar (the CSS default)
    const place = board
      ? `left:${board.layout.x}px;top:${board.layout.y - BOARD_STYLE.statusHeight}px;width:${board.layout.size}px;height:${BOARD_STYLE.statusHeight}px;transform:none;`
      : '';
    if (place !== statusPlace) {
      statusPlace = place;
      status.style.cssText = place;
    }
    root.classList.toggle('at-board', board !== null);
  }

  /** `why` is an optional line under the verdict (how the match ended). */
  function showResult(winner: Winner, me: PlayerId, handler: () => void, why: string | null = null): void {
    surrender.classList.add('hidden');
    countdown.classList.add('hidden');
    onAgain = handler;
    title.textContent = winner === 'draw' ? 'Unentschieden' : winner === me ? 'Gewonnen!' : 'Verloren';
    detail.textContent = why ?? '';
    detail.classList.toggle('hidden', why === null);
    overlay.classList.remove('hidden');
  }

  /** The opponent's name in the bar: "Bot" offline, "Gegner" online (and whether they are still there). */
  function setFoeLabel(label: string): void {
    setText(right.name, label);
    foeName = label.split(' · ')[0];
  }

  /** Small connection line in a corner: the round trip, or nothing in the local mode. */
  function setNet(text: string): void {
    setText(net, text);
  }

  function hideResult(): void {
    overlay.classList.add('hidden');
  }

  /** A shot was asked for but the slot could not deliver one: blink it so the click never feels dead. */
  function flashSlot(): void {
    replay(left.weapon, 'flash');
  }

  /** The promotion picker with the own colour's pieces; `onCancel` fires for a tap beside it. */
  function showPromotion(color: ChessColor, onPick: (piece: PromotionPiece) => void, onCancel: () => void): void {
    promotionPressed = false;
    onPromotion = onPick;
    onPromotionCancel = onCancel;
    for (const { img, code } of promotionImages) img.src = pieceUrl(import.meta.env.BASE_URL, `${color}${code}`);
    promotion.classList.remove('hidden');
  }

  function hidePromotion(): void {
    promotionPressed = false;
    promotion.classList.add('hidden');
    onPromotion = null;
    onPromotionCancel = null;
  }

  function setSurrender(handler: () => void, enabled = true): void {
    onSurrender = handler;
    surrender.disabled = !enabled;
  }

  return { update, showResult, hideResult, flashSlot, setFoeLabel, setNet, showPromotion, hidePromotion, setSurrender };
}
